import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row } from "react-bootstrap";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";

const initialForm = {
  name: "",
  description: "",
  parent_folder_uuid: ""
};

function DocumentFolderFormPage() {
  const history = useHistory();
  const location = useLocation();
  const { uuid } = useParams();
  const parentFolderUUIDFromQuery = new URLSearchParams(location.search).get("parent_uuid") || "";
  const isEditing = Boolean(uuid);
  const isCreatingChildFolder = !isEditing && Boolean(parentFolderUUIDFromQuery);
  const [form, setForm] = useState(initialForm);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchFolders();
    if (isEditing) {
      fetchFolder();
      return;
    }

    if (parentFolderUUIDFromQuery) {
      setForm((current) => ({
        ...current,
        parent_folder_uuid: parentFolderUUIDFromQuery
      }));
    }
  }, [uuid, parentFolderUUIDFromQuery]);

  const submitLabel = useMemo(() => (isEditing ? "Update folder" : "Create folder"), [isEditing]);

  async function fetchFolder() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(`/document-folders/${uuid}`);
      setForm({
        name: response.data.name || "",
        description: response.data.description || "",
        parent_folder_uuid: response.data.parent_folder_uuid || ""
      });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load document folder.");
    } finally {
      setLoading(false);
    }
  }

  async function fetchFolders() {
    try {
      const response = await api.get("/document-folders?scope=all");
      setFolders(response.data || []);
    } catch (err) {
      setFolders([]);
    }
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: value
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await api.put(`/document-folders/${uuid}`, form);
        toast.success("Document folder updated");
        history.push(`/documents/${uuid}`);
      } else {
        await api.post("/document-folders", form);
        toast.success("Document folder created");
        history.push(form.parent_folder_uuid ? `/documents/${form.parent_folder_uuid}` : "/documents");
      }
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${isEditing ? "update" : "create"} document folder.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Row className="justify-content-center">
      <Col xs={12} xl={10}>
        <PageHeader
          title={isEditing ? "Edit Document Folder" : "Add Document Folder"}
          action={
            <Button variant="outline-secondary" onClick={() => history.push("/documents")}>
              Back to documents
            </Button>
          }
        />

        {loading ? <LoadingState label="Loading document folder..." /> : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}

        {!loading ? (
          <div className="content-card form-card">
            <form onSubmit={handleSubmit}>
              <Row className="g-3">
                {!isCreatingChildFolder ? (
                  <Col xs={12}>
                    <label className="form-label">Parent folder</label>
                    <Form.Select name="parent_folder_uuid" value={form.parent_folder_uuid} onChange={handleChange}>
                      <option value="">Root folder</option>
                      {folders
                        .filter((folder) => folder.uuid !== uuid)
                        .map((folder) => (
                          <option key={folder.uuid} value={folder.uuid}>
                            {folder.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Col>
                ) : null}
                <Col xs={12}>
                  <label className="form-label">Folder name</label>
                  <Form.Control name="name" value={form.name} onChange={handleChange} required />
                </Col>
                <Col xs={12}>
                  <label className="form-label">Description</label>
                  <Form.Control as="textarea" rows={4} name="description" value={form.description} onChange={handleChange} />
                </Col>
              </Row>

              <div className="d-flex gap-2 mt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving..." : submitLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => history.push("/documents")}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        ) : null}
      </Col>
    </Row>
  );
}

export default DocumentFolderFormPage;
